import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { authHeader, isUnauthorized } from "../utils/staffAuth";

/**
 * "Been here before?" — the counter's customer lookup.
 *
 * A returning walk-in used to be typed out from scratch every visit, because
 * the only prefill this app had was from a booked appointment. About a quarter
 * of paid orders come from someone who has ordered before, so that was a
 * minute of retyping on one order in four -- and the commonest way a second
 * customer record gets made for one person.
 *
 * Staff search, pick a person, and their name, email and number fill in. They
 * can still change every field afterwards: this sets a form's starting values,
 * it never writes to the customer record.
 *
 * ## Why it shows more than the name, but not much more
 *
 * Two customers called Sarah is the ordinary case, and 62 phone numbers in
 * this database belong to more than one record -- some of them households. So
 * each row needs something beyond the name, and the human chooses. The lookup
 * narrows; it does not decide.
 *
 * But this laptop faces a customer. Listing other people's email addresses
 * and phone numbers in front of them is not ours to do, so the search answers
 * with masked details only -- "i•••@outlook.com · •••5263" -- which is enough
 * to confirm out loud and not enough to contact anybody. The masking is done
 * by the server, not here: anything sent to this page is also in the
 * browser's network tab. The real details are fetched for the one person
 * staff pick, who is the customer standing at the counter.
 *
 * ## Nothing is remembered
 *
 * The search box and its results are component state, so `resetKioskSession`'s
 * wipe between customers takes them with it. One customer's search must never
 * be sitting on screen for the next one.
 */

const pathURL = process.env.REACT_APP_API_URL;

/** Below three characters a search matches half the studio, and the backend
 *  refuses it anyway. */
const MIN_QUERY = 3;
/** Long enough that typing a name doesn't fire a request per letter, short
 *  enough that staff don't notice waiting. */
const DEBOUNCE_MS = 250;

const monthYear = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

/** "2 orders · last Sep 2026", or "no orders yet". */
const historyLine = (customer) => {
  if (!customer.order_count) return "no orders yet";
  const orders = `${customer.order_count} order${customer.order_count === 1 ? "" : "s"}`;
  const at = customer.last_order_at ? new Date(customer.last_order_at) : null;
  if (!at || Number.isNaN(at.getTime())) return orders;
  return `${orders} · last ${monthYear.format(at)}`;
};

function CustomerLookup({ onPick, filledFrom, onClear }) {
  const [query, setQuery] = useState("");
  const [state, setState] = useState({
    customers: [],
    total: 0,
    tooMany: false,
    searched: false,
    loading: false,
    error: null,
  });
  // Only the newest search may paint: a short query typed quickly can answer
  // after a longer one and put back results the staff member has moved past.
  const requestRef = useRef(0);
  const [picking, setPicking] = useState(false);

  /** Fetch the chosen customer's real details and hand them to the form. The
   *  search only ever had masked ones. */
  const choose = async (row) => {
    setPicking(true);
    try {
      const response = await axios.get(
        `${pathURL}order/customer-lookup/${row._id}`,
        { headers: authHeader() },
      );
      onPick(response.data.customer);
      setQuery("");
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Couldn't fetch their details — type them in instead.",
      }));
    } finally {
      setPicking(false);
    }
  };

  const search = useCallback(
    async (text) => {
      const ticket = (requestRef.current += 1);
      if (text.trim().length < MIN_QUERY) {
        setState({
          customers: [],
          total: 0,
          tooMany: false,
          searched: false,
          loading: false,
          error: null,
        });
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const response = await axios.get(`${pathURL}order/customer-lookup`, {
          params: { q: text.trim() },
          headers: authHeader(),
        });
        if (ticket !== requestRef.current) return;
        setState({
          customers: response.data?.customers || [],
          total: response.data?.total || 0,
          tooMany: Boolean(response.data?.too_many),
          searched: Boolean(response.data?.searched),
          loading: false,
          error: null,
        });
      } catch (error) {
        if (ticket !== requestRef.current) return;
        // Even a dead login only disables the lookup. This runs in the middle
        // of taking an order, and signing the device out -- or reloading to
        // show the login -- would throw away the garments, services and
        // prices already on screen. The payment step has its own handling for
        // an expired login; a convenience must never cost the order.
        setState({
          customers: [],
          total: 0,
          tooMany: false,
          searched: false,
          loading: false,
          error: isUnauthorized(error)
            ? "Search needs a fresh sign-in — type their details in instead."
            : "Couldn't search just now — type their details in instead.",
        });
      }
    },
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => search(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  if (filledFrom) {
    return (
      <p className="para customer-lookup-filled">
        Filled in from {filledFrom}&rsquo;s last order. Check it with them.{" "}
        <button
          type="button"
          className="customer-lookup-clear"
          onClick={() => {
            setQuery("");
            onClear?.();
          }}
        >
          Clear
        </button>
      </p>
    );
  }

  return (
    <div className="customer-lookup">
      <div className="field-box">
        <label className="label-on-line" htmlFor="customer-lookup-input">
          Been here before?
        </label>
        <input
          id="customer-lookup-input"
          type="text"
          autoComplete="off"
          placeholder="Search name, email or phone"
          value={query}
          maxLength={100}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {state.error && <p role="alert">{state.error}</p>}

      {state.loading && <p className="para customer-lookup-note">Searching…</p>}

      {!state.loading && state.tooMany && (
        <p className="para customer-lookup-note">
          {state.total} people match that. Type a bit more — a surname, or the
          last four digits of their number.
        </p>
      )}

      {!state.loading &&
        state.searched &&
        !state.tooMany &&
        !state.customers.length && (
          <p className="para customer-lookup-note">
            Nobody found — they&rsquo;re new, so fill it in below.
          </p>
        )}

      {!state.loading && state.customers.length > 0 && (
        <>
          <ul className="customer-lookup-results">
            {state.customers.map((customer) => (
              <li key={customer._id}>
                <button
                  type="button"
                  className="customer-lookup-result"
                  onClick={() => choose(customer)}
                  disabled={picking}
                >
                  <span className="customer-lookup-name">
                    {customer.name || "No name"}
                  </span>
                  <span className="customer-lookup-detail">
                    {customer.email_masked}
                    {customer.phone_masked
                      ? ` · ${customer.phone_masked}`
                      : ""}
                  </span>
                  <span className="customer-lookup-detail">
                    {historyLine(customer)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {state.total > state.customers.length && (
            <p className="para customer-lookup-note">
              Showing {state.customers.length} of {state.total}. Type a bit more
              to narrow it.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default CustomerLookup;
